import { FLAI_DAILY_LIMIT_MESSAGE } from '@domains/application-knowledge/shared';
import { DOCUMENT } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, OnDestroy, Output, Renderer2, RendererStyleFlags2, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { EnumPricingType, SUBSCRIPTION_MODULES, AGENT_WORKFLOW_DESTINATIONS, EnumFlyingAgentExplainStreamEventType, EnumFlyingAgentPageAction, FLYING_AGENT_RESEARCH_PAGES, IFlyingAgentExplainRequest, IFlyingAgentExplainStreamEvent, IFlyingAgentSelectedItem } from '@apps/flying/shared';
import { marked } from 'marked';
import { Observable, Subscription, firstValueFrom } from 'rxjs';

import { AgentPageActionService, IAgentResearchPage } from '../../../services/shared/agent/agent-page-action.service';
import { AgentRequest } from '../../../requests/shared/agent/agent.request';
import { ApiTokenBalanceReconciliationService } from '../../../services/shared/states/api-token-balance-reconciliation.service';
import { StatesService } from '../../../services/shared/states/states.service';
import { IWebAppTool } from '../../../shared/constants/app.constant';


interface IAgentAction {
	title		: string;
	description	: string;
	icon		: string;
	routerLink	: string;
}

interface IAgentMessage {
	id		: string;
	role	: 'user' | 'assistant';
	text	: string;
	steps?	: string[];
	tip?	: string;
	context?: string;
	creditsUsed?: number;
}

interface IPendingAgentAction {
	code		: EnumFlyingAgentPageAction;
	title		: string;
	description	: string;
}


@Component({
	selector: 'app-agent-panel',
	templateUrl: './agent-panel.component.html',
	styleUrls: ['./agent-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AgentPanelComponent implements AfterViewInit, OnDestroy {

	@Output() readonly panelClose: EventEmitter<void> = new EventEmitter<void>();

	@ViewChild('panel') panel: ElementRef<HTMLElement>;
	@ViewChild('panelBody') panelBody: ElementRef<HTMLElement>;
	readonly selectedApp$: Observable<IWebAppTool>;

	private viewportUpdateFrame: number | null = null;
	private readonly subscriptions: Subscription = new Subscription();
	private conversationStorageKey: string | null = null;
	private accountSessionVersion = 0;
	private readonly legacyConversationStorageKey: string = 'flying-agent-conversation-v1';
	private readonly panelWidthStorageKey: string = 'flying-agent-panel-width';
	private readonly textSizeStorageKey: string = 'flying-agent-text-size';
	private readonly agentActionRoutePrefix: string = '/__agent/action/';
	private readonly defaultPanelWidth: number = 400;
	private readonly maximumPanelWidth: number = 720;
	private readonly minimumResizableViewportWidth: number = 576;
	private documentCursorBeforeResize: string | null = null;
	private documentUserSelectBeforeResize: string | null = null;
	private workflowAbort?: AbortController;
	private activeRequest?: Subscription;
	private lastRequest: IFlyingAgentExplainRequest | null = null;
	private readonly documentScrollListener: EventListener = () => {
		this.schedulePanelUpdate();
	};

	question: string = '';
	messages: IAgentMessage[] = [];
	isLoading: boolean = false;
	errorMessage: string = '';
	actionStatusMessage: string = '';
	pendingAction: IPendingAgentAction | null = null;
	readonly panelMinWidth: number = 320;
	panelMaxWidth: number = this.maximumPanelWidth;
	panelWidth: number = this.defaultPanelWidth;
	textSize: number = 100;
	readonly minTextSize: number = 90;
	readonly maxTextSize: number = 150;
	isPanelResizing: boolean = false;

	readonly actions: IAgentAction[] = [
		{
			title		: 'Find a profitable niche',
			description	: 'Explore focused ideas with AI Niche Finder.',
			icon		: 'magnifying-glass-chart',
			routerLink	: '/research/ai/niche-finder'
		},
		{
			title		: 'Generate design ideas',
			description	: 'Turn a concept into visual prompt directions.',
			icon		: 'wand-magic-sparkles',
			routerLink	: '/research/ai/prompt-search'
		},
		{
			title		: 'Build listing templates',
			description	: 'Create reusable AI-powered listing content.',
			icon		: 'rectangle-list',
			routerLink	: '/upload/pod/ai-templates'
		}
	];

	constructor(
		@Inject(DOCUMENT) private document: Document,
		private hostElement: ElementRef<HTMLElement>,
		private renderer: Renderer2,
		private router: Router,
		private agentRequest: AgentRequest,
		private tokenBalanceReconciliation: ApiTokenBalanceReconciliationService,
		private statesService: StatesService,
		private cdr: ChangeDetectorRef,
		private agentPageActions: AgentPageActionService
	) {
		this.selectedApp$ = this.statesService.appState.getSelectedAppObservable();
	}


	ngAfterViewInit(): void {
		this.subscriptions.add(this.statesService.user.getUserInfo().subscribe((user) => {
			this.switchConversationAccount(user._id || user.email || '');
		}));
		this.loadPanelWidth();
		this.loadTextSize();
		this.panel.nativeElement.focus();
		this.updatePanelLayout();
		// Render restored messages before measuring their height on the next frame.
		this.cdr.detectChanges();
		this.scrollConversationToBottom();
		this.document.addEventListener('scroll', this.documentScrollListener, {
			capture	: true,
			passive	: true
		});
	}

	ngOnDestroy(): void {
		if (this.isLoading) this.reconcileTokenBalanceAfterSettlement();
		this.accountSessionVersion++;
		this.workflowAbort?.abort();
		this.subscriptions.unsubscribe();
		this.document.removeEventListener('scroll', this.documentScrollListener, true);
		this.setApplicationLayoutResizing(false);
		this.restoreDocumentResizeStyles();
		if (this.viewportUpdateFrame !== null) cancelAnimationFrame(this.viewportUpdateFrame);
	}

	startPanelResize(event: PointerEvent): void {
		if (event.button !== 0 || !this.canResizePanel()) return;

		event.preventDefault();
		(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
		this.isPanelResizing = true;
		this.documentCursorBeforeResize = this.document.body.style.cursor;
		this.documentUserSelectBeforeResize = this.document.body.style.userSelect;
		this.setApplicationLayoutResizing(true);
		this.renderer.setStyle(this.document.body, 'cursor', 'col-resize');
		this.renderer.setStyle(this.document.body, 'user-select', 'none');
	}

	resizePanel(event: PointerEvent): void {
		if (!this.isPanelResizing) return;

		event.preventDefault();
		const viewportWidth: number = this.document.defaultView?.innerWidth || 0;
		this.setPanelWidth(viewportWidth - event.clientX);
	}

	stopPanelResize(event?: PointerEvent): void {
		if (!this.isPanelResizing) return;

		if (event) {
			const handle: HTMLElement | null = this.panel.nativeElement.querySelector('.agent-panel-resize-handle');
			if (handle?.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
		}
		this.isPanelResizing = false;
		this.setApplicationLayoutResizing(false);
		this.restoreDocumentResizeStyles();
		this.persistPanelWidth();
	}

	onPanelResizeKeydown(event: KeyboardEvent): void {
		if (!this.canResizePanel()) return;

		const step: number = event.shiftKey ? 32 : 16;
		let width: number | null = null;
		if (event.key === 'ArrowLeft') width = this.panelWidth + step;
		if (event.key === 'ArrowRight') width = this.panelWidth - step;
		if (event.key === 'Home') width = this.panelMinWidth;
		if (event.key === 'End') width = this.panelMaxWidth;
		if (width === null) return;

		event.preventDefault();
		this.setPanelWidth(width);
		this.persistPanelWidth();
	}

	resetPanelWidth(): void {
		if (!this.canResizePanel()) return;

		this.setPanelWidth(this.defaultPanelWidth);
		this.persistPanelWidth();
	}

	explainCurrentFunction(): void {
		this.question = `Explain the current page, "${this.getCurrentPageTitle()}": what it is for and how to use it. Start with an overview of the page.`;
		this.submitQuestion();
	}

	submitQuestion(): void {
		const question: string = this.question.trim();
		if (!question || this.isLoading) return;
		if (question.length > 300) {
			this.errorMessage = 'Please keep your question to 300 characters or fewer.';
			this.cdr.markForCheck();
			return;
		}

		const request: IFlyingAgentExplainRequest = this.createExplainRequest(question);
		const accountSessionVersion = this.accountSessionVersion;
		let researchPage: IAgentResearchPage | null = this.agentPageActions.getResearchPage();
		const abort = new AbortController();
		this.workflowAbort = abort;
		const seenSteps = new Set<string>();
		let hasExecutedAction: boolean = false;
		let hasCompleted: boolean = false;
		this.lastRequest = request;
		this.question = '';
		this.errorMessage = '';
		this.actionStatusMessage = '';
		this.pendingAction = null;
		this.isLoading = true;
		const assistantMessage: IAgentMessage = {
			id	: this.createMessageId(),
			role: 'assistant',
			text: ''
		};
		let hasSettledTokenBalance: boolean = false;
		this.messages.push({
			id		: this.createMessageId(),
			role	: 'user',
			text	: question,
			context	: request.pageTitle
		}, assistantMessage);
		this.persistConversation();
		this.scrollConversationToBottom();
		this.cdr.markForCheck();

		this.activeRequest = this.agentRequest.explainFunction(request).subscribe({
			next: async (event: IFlyingAgentExplainStreamEvent) => {
				if (abort.signal.aborted) return;
				if (event.type === EnumFlyingAgentExplainStreamEventType.Action && request.workflow && event.command && event.stepId) {
					if (seenSteps.has(event.stepId)) return;
					seenSteps.add(event.stepId);
					try {
						let status: string;
						try { status = await this.agentPageActions.executeWorkflow(event.command, researchPage, abort.signal); }
						catch (error) { status = 'Action failed: ' + (error instanceof Error ? error.message : 'Unknown error'); }
						if (abort.signal.aborted) return;
						(assistantMessage.steps ||= []).push(status);
						this.lastRequest = null;
						const currentPage = this.agentPageActions.getResearchPage();
						const openedEditor = event.command.operation === 'open_editor' && !status.startsWith('Action failed:') && this.router.url.split(/[?#]/)[0] === '/upload/pod/edit';
						if (!openedEditor && (!currentPage || event.command.operation !== 'navigate' && currentPage !== researchPage || event.command.operation === 'navigate' && this.router.url.split(/[?#]/)[0] !== AGENT_WORKFLOW_DESTINATIONS[event.command.destination]?.route)) throw new Error('The page changed.');
						researchPage = currentPage;
						this.persistConversation();
						this.cdr.markForCheck();
						await firstValueFrom(this.agentRequest.completeAction({stepId: event.stepId, status,
							route: this.router.url, pageTitle: this.getCurrentPageTitle(), pageData: openedEditor ? JSON.stringify({ page: 'Edit Designs', agentControls: { operations: [], awaitingUser: true }, status }) : this.agentPageActions.getContextSnapshot() || '{}'}));
					} catch {
						if (abort.signal.aborted) return;
						this.stopWorkflow();
						assistantMessage.text = 'Workflow stopped because the page changed or its result could not be delivered. Completed steps are listed below.';
						this.persistConversation();
						this.cdr.markForCheck();
					}
					return;
				}
				if (event.type === EnumFlyingAgentExplainStreamEventType.Reset) assistantMessage.text = '';
				if (event.type === EnumFlyingAgentExplainStreamEventType.Delta) {
					assistantMessage.text += event.delta || '';
				}
				if (event.type === EnumFlyingAgentExplainStreamEventType.Completed) {
					if (hasCompleted) return;
					hasCompleted = true;
					assistantMessage.text = event.markdown || assistantMessage.text;
					assistantMessage.creditsUsed = event.creditsUsed;
					if (!request.workflow && event.action && !hasExecutedAction && this.messages.includes(assistantMessage)) {
						hasExecutedAction = true;
						if (request.availableActions?.includes(event.action.code) && this.router.url === request.route) {
							try {
								const status = this.agentPageActions.executeResearch(event.action, researchPage);
								assistantMessage.text = typeof status === 'string' ? status : await status;
							} catch {
								assistantMessage.text = 'The search could not be started. Please try again on the current research page.';
							}
						} else {
							assistantMessage.text = 'The page changed. Please ask again on the current research page.';
						}
					}
					if (abort.signal.aborted) return;
					if (Number.isFinite(event.creditBalanceAfter)) {
						this.statesService.tokens.setTokensInfo(event.creditBalanceAfter!);
						hasSettledTokenBalance = true;
					}
					this.isLoading = false;
					this.persistConversation();
				}
				this.scrollConversationToBottom(false);
				this.cdr.markForCheck();
			},
			error: (error: unknown) => {
				abort.abort();
				if (assistantMessage.steps?.length) assistantMessage.text = 'The workflow stopped. Completed steps are listed below.';
				else this.messages = this.messages.filter((message: IAgentMessage) => message.id !== assistantMessage.id);
				this.persistConversation();
				this.isLoading = false;
				this.errorMessage = error instanceof Error && error.message === FLAI_DAILY_LIMIT_MESSAGE
					? FLAI_DAILY_LIMIT_MESSAGE
					: error && typeof error === 'object' && 'code' in error && error.code === 'FLAI_RESPONSE_TOO_LONG'
					? 'The answer was too long. Try asking for a shorter summary or a comparison of fewer products.'
					: 'FLAI could not complete your request. Please try again.';
				this.tokenBalanceReconciliation.reconcileAfterSseError(error, (balance: number) => {
					if (accountSessionVersion !== this.accountSessionVersion) return;
					this.statesService.tokens.setTokensInfo(balance);
				});
				this.cdr.markForCheck();
			},
			complete: () => {
				if (!hasCompleted) this.isLoading = false;
				if (!hasSettledTokenBalance) this.reconcileTokenBalanceAfterSettlement();
				this.persistConversation();
				this.cdr.markForCheck();
			}
		});
		this.subscriptions.add(this.activeRequest);
	}

	stopWorkflow(): void {
		this.workflowAbort?.abort();
		this.activeRequest?.unsubscribe();
		this.isLoading = false;
		this.actionStatusMessage = "Workflow stopped. An action already in progress may still finish.";
		this.reconcileTokenBalanceAfterSettlement();
		this.cdr.markForCheck();
	}

	private reconcileTokenBalanceAfterSettlement(): void {
		const accountSessionVersion = this.accountSessionVersion;
		this.tokenBalanceReconciliation.reconcileAfterSettlement((balance: number) => {
			if (accountSessionVersion !== this.accountSessionVersion) return;
			this.statesService.tokens.setTokensInfo(balance);
		});
	}

	onComposerKeydown(event: KeyboardEvent): void {
		if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;

		event.preventDefault();
		this.submitQuestion();
	}

	onMarkdownNavigation(event: MouseEvent): void {
		const target: Element | null = event.target instanceof Element ? event.target : null;
		const anchor: HTMLAnchorElement | null = target?.closest<HTMLAnchorElement>('a[href]') || null;
		const route: string = anchor?.getAttribute('href') || '';
		if (!anchor || !route.startsWith('/') || route.startsWith('//')) return;

		event.preventDefault();
		const actionCode: EnumFlyingAgentPageAction | null = this.getAgentActionCode(route);
		if (route.startsWith(this.agentActionRoutePrefix)) {
			if (actionCode) this.prepareAgentAction(actionCode);
			return;
		}
		if (route.split(/[?#]/)[0] === this.router.url.split(/[?#]/)[0]) return;
		this.router.navigateByUrl(route);
	}

	confirmPendingAction(): void {
		if (!this.pendingAction) return;

		const actionCode: EnumFlyingAgentPageAction = this.pendingAction.code;
		this.pendingAction = null;
		this.executeAgentAction(actionCode);
	}

	cancelPendingAction(): void {
		this.pendingAction = null;
		this.actionStatusMessage = 'Action cancelled.';
		this.cdr.markForCheck();
	}

	get isDailyLimitReached(): boolean {
		return this.errorMessage === FLAI_DAILY_LIMIT_MESSAGE;
	}

	get canUpgradeDailyLimit(): boolean {
		const modules = this.statesService.access?.getAccessRights().getValue() || [];
		return this.isDailyLimitReached && !SUBSCRIPTION_MODULES.some((module) =>
			module.pricingType === EnumPricingType.Paid && modules.includes(module.code));
	}

	retryLastRequest(): void {
		if (!this.lastRequest || this.isLoading) return;
		this.question = this.lastRequest.question;
		this.submitQuestion();
	}

	startNewConversation(): void {
		if (this.isLoading) this.stopWorkflow();
		this.messages = [];
		this.errorMessage = '';
		this.actionStatusMessage = '';
		this.pendingAction = null;
		this.lastRequest = null;
		this.persistConversation();
		this.cdr.markForCheck();
	}

	trackMessage(index: number, message: IAgentMessage): string {
		return message.id;
	}

	hasStreamingResponse(): boolean {
		const lastMessage: IAgentMessage | undefined = this.messages[this.messages.length - 1];
		return lastMessage?.role === 'assistant' && lastMessage.text.length > 0;
	}

	renderMarkdown(markdown: string): string {
		const renderer = new marked.Renderer();
		renderer.code = () => '<p>FLAI provides plain-language guidance. Please ask for an explanation without code.</p>';
		renderer.codespan = (text: string) => text;
		renderer.html = () => '';
		// Model-generated image URLs could transmit page data without a user action.
		renderer.image = () => '';
		const renderLink = renderer.link.bind(renderer);
		renderer.link = (href: string, title: string, text: string) => {
			// Keep navigation inside the application; arbitrary URLs can carry private context.
			return href?.startsWith('/') && !href.startsWith('//') && !href.includes('\\')
				? renderLink(href, title, text) : text;
		};
		return marked.parse(markdown, {
			renderer,
			headerIds	: false,
			mangle		: false
		}) as string;
	}

	@HostListener('window:resize')
	onViewportChange(): void {
		this.schedulePanelUpdate();
	}

	@HostListener('window:pointermove', ['$event'])
	onPanelResizePointerMove(event: PointerEvent): void {
		this.resizePanel(event);
	}

	@HostListener('window:pointerup', ['$event'])
	@HostListener('window:pointercancel', ['$event'])
	onPanelResizePointerEnd(event: PointerEvent): void {
		this.stopPanelResize(event);
	}

	private schedulePanelUpdate(): void {
		if (this.viewportUpdateFrame !== null) return;

		this.viewportUpdateFrame = requestAnimationFrame(() => {
			this.updatePanelLayout();
			this.viewportUpdateFrame = null;
		});
	}

	private updatePanelLayout(): void {
		this.updatePanelTop();
		if (!this.canResizePanel()) {
			this.stopPanelResize();
			return;
		}

		const viewportWidth: number = this.document.defaultView?.innerWidth || this.maximumPanelWidth;
		this.panelMaxWidth = Math.max(this.panelMinWidth, Math.min(this.maximumPanelWidth, viewportWidth - 48));
		this.setPanelWidth(this.panelWidth);
	}

	private updatePanelTop(): void {
		const header: HTMLElement | null = this.document.querySelector('app-header');
		const panelTop: number = Math.max(0, header?.getBoundingClientRect().bottom ?? 0);
		this.renderer.setStyle(
			this.hostElement.nativeElement,
			'--agent-panel-top',
			`${panelTop}px`,
			RendererStyleFlags2.DashCase
		);
	}

	private canResizePanel(): boolean {
		return (this.document.defaultView?.innerWidth || 0) >= this.minimumResizableViewportWidth;
	}

	private setPanelWidth(width: number): void {
		this.panelWidth = Math.round(Math.min(this.panelMaxWidth, Math.max(this.panelMinWidth, width)));
		const layoutElement: HTMLElement = this.getApplicationLayoutElement();
		for (const element of [this.hostElement.nativeElement, layoutElement]) {
			this.renderer.setStyle(
				element,
				'--agent-panel-width',
				`${this.panelWidth}px`,
				RendererStyleFlags2.DashCase
			);
		}
		this.cdr.markForCheck();
	}

	private getApplicationLayoutElement(): HTMLElement {
		return this.hostElement.nativeElement.closest<HTMLElement>('.main') || this.hostElement.nativeElement;
	}

	private setApplicationLayoutResizing(isResizing: boolean): void {
		const layoutElement: HTMLElement = this.getApplicationLayoutElement();
		if (isResizing) this.renderer.addClass(layoutElement, 'agent-panel-resizing');
		else this.renderer.removeClass(layoutElement, 'agent-panel-resizing');
	}

	setTextSize(size: number): void {
		if (!Number.isFinite(size)) return;
		this.textSize = Math.min(this.maxTextSize, Math.max(this.minTextSize, Math.round(size / 10) * 10));
		try {
			this.document.defaultView?.localStorage.setItem(this.textSizeStorageKey, String(this.textSize));
		} catch {
			// The adjustment still works when browser storage is unavailable.
		}
		this.cdr.markForCheck();
	}

	private loadTextSize(): void {
		try {
			const stored = this.document.defaultView?.localStorage.getItem(this.textSizeStorageKey);
			const size = Number(stored);
			if (stored && Number.isFinite(size) && size >= this.minTextSize && size <= this.maxTextSize) {
				this.textSize = Math.round(size / 10) * 10;
			}
		} catch {
			// Keep the default size when browser storage is unavailable.
		}
	}

	private loadPanelWidth(): void {
		try {
			const storedWidth: string | null = this.document.defaultView?.localStorage.getItem(this.panelWidthStorageKey) || null;
			const width: number = Number(storedWidth);
			if (storedWidth && Number.isFinite(width)) this.panelWidth = width;
		} catch {
			// Local storage can be unavailable in privacy-restricted browser contexts.
		}
	}

	private persistPanelWidth(): void {
		try {
			this.document.defaultView?.localStorage.setItem(this.panelWidthStorageKey, String(this.panelWidth));
		} catch {
			// Local storage can be unavailable in privacy-restricted browser contexts.
		}
	}

	private restoreDocumentResizeStyles(): void {
		if (this.documentCursorBeforeResize === null || this.documentUserSelectBeforeResize === null) return;

		if (this.documentCursorBeforeResize) this.renderer.setStyle(this.document.body, 'cursor', this.documentCursorBeforeResize);
		else this.renderer.removeStyle(this.document.body, 'cursor');
		if (this.documentUserSelectBeforeResize) this.renderer.setStyle(this.document.body, 'user-select', this.documentUserSelectBeforeResize);
		else this.renderer.removeStyle(this.document.body, 'user-select');
		this.documentCursorBeforeResize = null;
		this.documentUserSelectBeforeResize = null;
	}

	private createExplainRequest(question: string): IFlyingAgentExplainRequest {
		const pageTitle: string = this.getCurrentPageTitle();
		const availableActions = this.getAvailablePageActions();
		const page = this.agentPageActions.getResearchPage();
		const pageData = JSON.parse((page?.workflow ? this.agentPageActions.getContextSnapshot() : page?.getContext?.()) || '{}');
		pageData.currentPageAccess = {
			pageAvailable: this.statesService.access?.hasAccess(this.router.url.split(/[?#]/)[0]),
			searchAvailable: availableActions.includes(EnumFlyingAgentPageAction.FocusSearch)
		};
		const selectedText: string = this.document.defaultView?.getSelection()?.toString().trim().slice(0, 1000) || '';
		const request: IFlyingAgentExplainRequest = {
			question,
			route		: this.router.url.slice(0, 500),
			pageTitle,
			history: this.messages
				.filter((message: IAgentMessage) => (message.role === 'user' || message.role === 'assistant') && Boolean(message.text?.trim()))
				.slice(-10)
				.map((message: IAgentMessage) => ({ role: message.role, text: [message.text, ...(message.steps || [])].join('\n').slice(0, 2000), ...message.context ? { pageTitle: message.context.slice(0, 160) } : {} })),
			pageData: JSON.stringify(pageData),
			workflow: this.agentPageActions.getResearchPage()?.workflow ? true : undefined,
			availableActions
		};
		if (selectedText) request.selection = selectedText;
		const selectedItem: IFlyingAgentSelectedItem | null = this.getSelectedApplicationItem();
		if (selectedItem) request.selectedItem = selectedItem;
		return request;
	}

	private getSelectedApplicationItem(): IFlyingAgentSelectedItem | null {
		const selectedElements: HTMLElement[] = Array.from(this.document.querySelectorAll<HTMLElement>('[data-agent-selected="true"]'));
		const selectedElement: HTMLElement | undefined = selectedElements.find((element: HTMLElement) => !this.panel.nativeElement.contains(element) && element.offsetParent !== null);
		if (!selectedElement) return null;

		const label: string = (
			selectedElement.dataset.agentItemLabel
			|| selectedElement.getAttribute('aria-label')
			|| selectedElement.innerText
		).trim().replace(/\s+/g, ' ').slice(0, 240);
		if (!label) return null;

		const selectedItem: IFlyingAgentSelectedItem = {
			type	: (selectedElement.dataset.agentItemType || selectedElement.getAttribute('role') || selectedElement.tagName.toLowerCase()).slice(0, 80),
			label
		};
		const id: string = (selectedElement.dataset.agentItemId || selectedElement.id || '').trim().slice(0, 160);
		if (id) selectedItem.id = id;
		return selectedItem;
	}

	private getAvailablePageActions(): EnumFlyingAgentPageAction[] {
		const searchInput: HTMLInputElement | HTMLTextAreaElement | null = this.findPageSearchInput();
		const actions: EnumFlyingAgentPageAction[] = [];
		const researchPage = this.agentPageActions.getResearchPage();
		if (researchPage && researchPage.kind !== 'upload') actions.push(researchPage.action || EnumFlyingAgentPageAction.RunRealTimeResearch);
		if (!searchInput) return actions;

		actions.push(EnumFlyingAgentPageAction.FocusSearch);
		if (searchInput.value.trim()) actions.push(EnumFlyingAgentPageAction.ClearSearch);
		return actions;
	}

	private getCurrentPageTitle(): string {
		const headings: HTMLElement[] = Array.from(this.document.querySelectorAll<HTMLElement>('main h1, main h2, .main h1, .main h2'));
		const currentHeading: HTMLElement | undefined = headings.find((heading: HTMLElement) => !this.panel.nativeElement.contains(heading) && heading.innerText.trim().length > 0);
		return currentHeading?.innerText.trim().slice(0, 160) || this.document.title.slice(0, 160) || 'Current Flying page';
	}

	private switchConversationAccount(accountId: string): void {
		const key = accountId ? `flying-agent-conversation-v3:${encodeURIComponent(accountId)}` : null;
		if (key === this.conversationStorageKey) return;
		this.accountSessionVersion++;
		this.workflowAbort?.abort();
		this.activeRequest?.unsubscribe();
		this.isLoading = false;
		this.conversationStorageKey = key;
		this.messages = [];
		this.question = '';
		this.lastRequest = null;
		this.pendingAction = null;
		this.errorMessage = '';
		this.actionStatusMessage = '';
		this.loadConversation();
		this.cdr.markForCheck();
	}

	private loadConversation(): void {
		try {
			const storage: Storage | undefined = this.document.defaultView?.localStorage;
			// Legacy conversations have no owner and must never be assigned to the next login.
			storage?.removeItem('flying-agent-conversation-v2');
			this.document.defaultView?.sessionStorage.removeItem(this.legacyConversationStorageKey);
			if (!this.conversationStorageKey) return;
			const value: string | null = storage?.getItem(this.conversationStorageKey) || null;
			if (!value) return;
			const messages: IAgentMessage[] = JSON.parse(value);
			if (Array.isArray(messages)) this.messages = messages.slice(-20);
		} catch {
			this.messages = [];
		}
	}

	private persistConversation(): void {
		if (!this.conversationStorageKey) return;
		try {
			const storage: Storage | undefined = this.document.defaultView?.localStorage;
			if (this.messages.length === 0) storage?.removeItem(this.conversationStorageKey);
			else storage?.setItem(this.conversationStorageKey, JSON.stringify(this.messages.slice(-20)));
		} catch {
			// Local storage can be unavailable in privacy-restricted browser contexts.
		}
	}

	private getAgentActionCode(route: string): EnumFlyingAgentPageAction | null {
		if (!route.startsWith(this.agentActionRoutePrefix)) return null;

		const actionCode: string = route.slice(this.agentActionRoutePrefix.length).split(/[?#]/)[0];
		return Object.values(EnumFlyingAgentPageAction).includes(actionCode as EnumFlyingAgentPageAction)
			? actionCode as EnumFlyingAgentPageAction
			: null;
	}

	private prepareAgentAction(actionCode: EnumFlyingAgentPageAction): void {
		this.actionStatusMessage = '';
		if (actionCode in FLYING_AGENT_RESEARCH_PAGES) return;
		if (!this.getAvailablePageActions().includes(actionCode)) {
			this.actionStatusMessage = 'This page action is not available with the current controls.';
			this.cdr.markForCheck();
			return;
		}
		if (actionCode === EnumFlyingAgentPageAction.FocusSearch) {
			this.executeAgentAction(actionCode);
			return;
		}

		this.pendingAction = {
			code		: actionCode,
			title		: 'Clear page search?',
			description	: 'This will remove the current search text. You can enter it again afterward.'
		};
		this.cdr.markForCheck();
		this.scrollConversationToBottom();
	}

	private executeAgentAction(actionCode: EnumFlyingAgentPageAction): void {
		const searchInput: HTMLInputElement | HTMLTextAreaElement | null = this.findPageSearchInput();
		if (!searchInput) {
			this.actionStatusMessage = 'The page search is not available right now.';
			this.cdr.markForCheck();
			return;
		}

		searchInput.focus();
		searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
		if (actionCode === EnumFlyingAgentPageAction.ClearSearch) {
			searchInput.value = '';
			searchInput.dispatchEvent(new Event('input', { bubbles: true }));
			searchInput.dispatchEvent(new Event('change', { bubbles: true }));
			this.actionStatusMessage = 'Page search cleared.';
		} else {
			this.actionStatusMessage = 'Page search focused.';
		}
		this.cdr.markForCheck();
	}

	private findPageSearchInput(): HTMLInputElement | HTMLTextAreaElement | null {
		const selectors: string = ['input[type="search"]', 'input[placeholder*="search" i]', 'textarea[placeholder*="search" i]'].join(',');
		const inputs: Array<HTMLInputElement | HTMLTextAreaElement> = Array.from(this.document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(selectors));
		return inputs.find((input: HTMLInputElement | HTMLTextAreaElement) => {
			if (this.panel.nativeElement.contains(input) || input.type === 'hidden' || input.readOnly || input.matches(':disabled')) return false;
			if (input.closest('[inert], [hidden], [aria-disabled="true"], .limit-by-disable, .limit-by-blur, .disabled-container, .disabled-container-blur, .disabled-page-container')) return false;
			for (let element: HTMLElement | null = input; element; element = element.parentElement) {
				const style = this.document.defaultView?.getComputedStyle(element);
				if (style?.display === 'none' || style?.visibility === 'hidden' || style?.visibility === 'collapse') return false;
			}
			return true;
		}) || null;
	}

	private scrollConversationToBottom(force: boolean = true): void {
		const body = this.panelBody?.nativeElement;
		if (!body) return;
		const startingTop = body.scrollTop;
		if (!force && body.scrollHeight - body.clientHeight - startingTop > 48) return;
		requestAnimationFrame(() => {
			// A wheel/keyboard scroll between the delta and this frame takes precedence.
			if (this.panelBody?.nativeElement !== body || !force && body.scrollTop < startingTop - 1) return;
			body.scrollTop = body.scrollHeight;
		});
	}

	private createMessageId(): string {
		return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
	}
}
